import forEach from 'lodash/forEach';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';

import {
  getAvailableIvCategories,
  getAvailableIvPorts,
  getAvailableIvSites,
  getAvailableIvSides,
} from '~lib/common/utils/availableIvOptions';

import { unitTypes } from '~lib/common/config/medication'
import { medicationMsg, fluidMsg, caseLogicMsg } from '../validationMessages';

const CHECKS_WITH_MEDS = [
  'INFUSION_MEDICATION',
  'INFUSION_MEDICATION_SETUP',
  'INFUSION_MEDICATION_RATE',
  'INFUSION_MEDICATION_ACTION_TYPE',
];

const CHECKS_WITH_FLUIDS = [
  'INFUSION_FLUID',
  'INFUSION_FLUID_SETUP',
  'INFUSION_FLUID_RATE',
  'INFUSION_FLUID_ACTION_TYPE',
];

const MED_CHECKS_WITH_BAG_VOLUME = [
  'INFUSION_MEDICATION',
  'INFUSION_MEDICATION_SETUP',
  'INFUSION_MEDICATION_RATE',
];

const FLUID_CHECKS_WITH_BAG_VOLUME = [
  'INFUSION_FLUID',
  'INFUSION_FLUID_SETUP',
  'INFUSION_FLUID_RATE',
];

const CHECKS_WITH_LOCATION = [
  'INFUSION_MEDICATION',
  'INFUSION_MEDICATION_SETUP',
  'INFUSION_MEDICATION_RATE',
  'INFUSION_MEDICATION_ACTION_TYPE',
  'INFUSION_FLUID',
  'INFUSION_FLUID_SETUP',
  'INFUSION_FLUID_RATE',
  'INFUSION_FLUID_ACTION_TYPE',
];

const CHECKS_WITH_DRIPSET = [
  'INFUSION_MEDICATION_SETUP',
  'INFUSION_FLUID_SETUP',
];

function validateInfusionNodes(caseConf, nodeInfo, { isEffectChain, isMedicationEffectChain }) {
  const errors = [];
  const warnings = [];

  const caseDosageFormsByMedicationId = keyBy(caseConf.dosageForms || [], 'medicationId');
  const caseFluidsByFluidKey = keyBy(caseConf.fluids || [], 'key');

  // Actions
  nodeInfo.actionNodes.forEach((node) => {
    const action = get(node, 'data.action');
    if (action && action.type === 'INFUSION_MEDICATION') {
      const dosageForm = caseDosageFormsByMedicationId[action.id];
      if (!dosageForm) {
        errors.push(
          medicationMsg('Medication is used in case logic but could not be found', action.id)
        );
        errors.push(
          caseLogicMsg('Medication not enabled', node.id, { errorId: 'medFluid', medicationId: action.id })
        );
      }
    }
    if (action && action.type === 'INFUSION_FLUID') {
      const fluid = caseFluidsByFluidKey[action.id];
      if (!fluid) {
        errors.push(
          fluidMsg('Fluid is used in case logic but could not be found', action.id)
        );
        errors.push(
          caseLogicMsg('Fluid not enabled', node.id, { errorId: 'medFluid', fluidId: action.id })
        );
      }
    }
  });

  // Checks
  nodeInfo.checkNodes.forEach((node) => {
    forEach(node.data, (check, checkKey) => {
      let dosageForm = caseDosageFormsByMedicationId[check.id];
      let infusionRoute = dosageForm ? dosageForm.enabledRoutes.find(route => route.key === 'IV_IO_INFUSION') : null;
      let fluid = caseFluidsByFluidKey[check.id];

      // INFUSION W/MEDICATION
      if (CHECKS_WITH_MEDS.includes(check.type)) {
        if (!dosageForm) {
          const medMsg = medicationMsg(`Medication "${check.id}" is used in case logic but could not be found`, check.id);
          const clMsg =  caseLogicMsg('Medication not enabled', node.id, { errorId: 'medFluid', medicationId: check.id, checkKey });
          if (isEffectChain) {
            // Case logic effect chains get warnings, default medication effect chains do not
            if (!isMedicationEffectChain) {
              const warningExists = warnings.find(w => w.id === check.id);
              if (!warningExists) {
                warnings.push(medMsg);
                warnings.push(clMsg);
              }
            }
          } else {
            errors.push(medMsg);
            errors.push(clMsg);
          }
          return; // skip remaining checks
        }
      }

      // INFUSION W/FLUID
      if (CHECKS_WITH_FLUIDS.includes(check.type)) {
        if (!fluid) {
          const clMsg = caseLogicMsg('Fluid not enabled', node.id, { errorId: 'medFluid', fluidId: check.id, checkKey });
          if (isEffectChain) {
            // Case logic effect chains get warnings, default medication effect chains do not
            if (!isMedicationEffectChain) {
              warnings.push(clMsg);
            }
          } else {
            errors.push(clMsg);
          }
          return; // skip remaining checks
        }
      }

      if (CHECKS_WITH_MEDS.includes(check.type) || CHECKS_WITH_FLUIDS.includes(check.type)) {
        if (!caseConf.iv.enabled) {
          errors.push(
            caseLogicMsg('IV not enabled', node.id, { errorId: 'ivDisabled', checkKey })
          );
        }
      }

      // STATUS SPECIFIC
      if (check.type === 'INFUSION_FLUID' && check.metric === 'DELIVERED') {
        const checkUnitType = unitTypes.find(ut => ut.key === check.unit);
        if (checkUnitType && checkUnitType.category !== 'VOLUME') {
          errors.push(caseLogicMsg('Unit type not available', node.id, { errorId: 'unitDisabled', checkKey }));
        }
      }
      if (check.type === 'INFUSION_MEDICATION' && check.metric === 'DELIVERED' && dosageForm) {
        const checkUnitType = unitTypes.find(ut => ut.key === check.unit);
        const availableCategories = [];
        const { concentration } = dosageForm;
        const numeratorUnitType = unitTypes.find(ut => ut.key === concentration.numeratorUnits);
        if (numeratorUnitType) {
          availableCategories.push(numeratorUnitType.category);
        }
        const denominatorUnitType = unitTypes.find(ut => ut.key === concentration.denominatorUnits);
        if (denominatorUnitType) {
          availableCategories.push(denominatorUnitType.category);
        }
        if (checkUnitType && !availableCategories.includes(checkUnitType.category)) {
          errors.push(caseLogicMsg('Unit type not available', node.id, { errorId: 'unitDisabled', checkKey }));
        }
      }

      // INITIATED SPECIFIC
      if ((check.type === 'INFUSION_MEDICATION_SETUP' || check.type === 'INFUSION_FLUID_SETUP') && check.method === 'DROP_SET') {
        // Drip set not enabled
        if (!caseConf.iv.dropSet || !caseConf.iv.dropSet.enabled) {
          errors.push(caseLogicMsg('Drip set not enabled', node.id, { errorId: 'methodDisabled', checkKey }));
        }
      }
      if ((check.type === 'INFUSION_MEDICATION_SETUP' || check.type === 'INFUSION_FLUID_SETUP') && check.method === 'IV_PUMP') {
        // Pumpt not enabled
        if (!caseConf.iv.pump || !caseConf.iv.pump.enabled) {
          errors.push(caseLogicMsg('Pump not enabled', node.id, { errorId: 'methodDisabled', checkKey }));
        }
        // Secondary not enabled
        const isSecondaryAvailable = get(caseConf.iv, 'pump.allowOptions') || false;
        if (!check.isPrimaryPump && !isSecondaryAvailable) {
          errors.push(caseLogicMsg('Secondary pump not enabled', node.id, { errorId: 'secondaryDisabled', checkKey }));
        }
        // Formulary not enabled
        if (check.function === 'FORMULARY' && check.formulary) {
          let availableFormularies = [];
          if (check.type === 'INFUSION_MEDICATION_SETUP' && dosageForm) {
            availableFormularies = infusionRoute ? infusionRoute.options.formularies : [];
          }
          if (check.type === 'INFUSION_FLUID_SETUP' && fluid) {
            availableFormularies = fluid.formularies;
          }
          const matching = availableFormularies.find(
            formulary => formulary.dose === check.formulary.dose &&
                         formulary.dosing === check.formulary.dosing &&
                         formulary.volume === check.formulary.volume
          );
          if (!matching) {
            errors.push(caseLogicMsg('Formulary not enabled', node.id, { errorId: 'formularyDisabled', checkKey }));
          }
        }
        // Dosing type not enabled
        if (check.function === 'CUSTOM_DOSE' && check.customDosingType) {
          let availableDosingTypes = [];
          if (check.type === 'INFUSION_MEDICATION_SETUP' && dosageForm) {
            availableDosingTypes = infusionRoute ? infusionRoute.options.infusionDosingTypes : [];
          }
          if (check.type === 'INFUSION_FLUID_SETUP' && fluid) {
            availableDosingTypes = fluid.infusionDosingTypes;
          }
          if (!availableDosingTypes.includes(check.customDosingType)) {
            errors.push(caseLogicMsg('Dosing type not enabled', node.id, { errorId: 'dosingTypeDisabled', checkKey }));
          }
        }
      }

      // ADJUSTED SPECIFIC
      if (check.type === 'INFUSION_MEDICATION_ACTION_TYPE' || check.type === 'INFUSION_FLUID_ACTION_TYPE') {
        if (!check.value) {
          errors.push(caseLogicMsg('Must select adjustment', node.id, { errorId: 'adjustmentDisabled', checkKey }));
        }
        else if (check.target === 'CURRENT' && check.value !== 'PAUSED' && check.value !== 'CLAMP_FULL_PAUSED') {
          errors.push(caseLogicMsg('Must select adjustment', node.id, { errorId: 'adjustmentDisabled', checkKey }));
        }
      }

      // BAG VOLUME
      if (MED_CHECKS_WITH_BAG_VOLUME.includes(check.type)) {
        if (dosageForm) {
          const availableBagVolumes = infusionRoute ? infusionRoute.options.infusionBagVolumes : [];
          for (const bagVolume of check.bagVolumes || []) {
            if (!availableBagVolumes.includes(bagVolume)) {
              errors.push(caseLogicMsg('Bag volume not enabled', node.id, { errorId: 'bagVolume', checkKey }));
            }
          }
        }
      }
      if (FLUID_CHECKS_WITH_BAG_VOLUME.includes(check.type)) {
        if (fluid) {
          const availableBagVolumes = fluid.infusionBagVolumes
          for (const bagVolume of check.bagVolumes || []) {
            if (!availableBagVolumes.includes(bagVolume)) {
              errors.push(caseLogicMsg('Bag volume not enabled', node.id, { errorId: 'bagVolume', checkKey }));
            }
          }
        }
      }

      // LOCATION - IV SITE
      if (CHECKS_WITH_LOCATION.includes(check.type) && check.ivInfusionLocation === 'IV_SITE') {
        // Categories
        const availableCategorieKeys = getAvailableIvCategories(caseConf).map(category => category.key);
        for (const categoryKey of check.ivInfusionLocationIv.categoryTypes || []) {
          if (!availableCategorieKeys.includes(categoryKey)) {
            errors.push(caseLogicMsg('IV category not enabled', node.id, { errorId: 'ivCategory', checkKey }));
          }
        }
        // Ports
        const availablePortKeys = getAvailableIvPorts(caseConf).map(port => port.key);
        for (const portKey of check.ivInfusionLocationIv.ports || []) {
          if (!availablePortKeys.includes(portKey)) {
            errors.push(caseLogicMsg('IV port not enabled', node.id, { errorId: 'ivPort', checkKey }));
          }
        }
        // Sites
        const availableSiteKeys = getAvailableIvSites(caseConf).map(site => site.key);
        for (const siteKey of check.ivInfusionLocationIv.sites || []) {
          if (!availableSiteKeys.includes(siteKey)) {
            errors.push(caseLogicMsg('IV site not enabled', node.id, { errorId: 'ivSite', checkKey }));
          }
        }
        // Sides
        const availableSideKeys = getAvailableIvSides(caseConf).map(side => side.key);
        for (const sideKey of check.ivInfusionLocationIv.sides || []) {
          if (!availableSideKeys.includes(sideKey)) {
            errors.push(caseLogicMsg('IV side not enabled', node.id, { errorId: 'ivSide', checkKey }));
          }
        }
      }

      // LOCATION - MEDICATION
      if (CHECKS_WITH_LOCATION.includes(check.type) && check.ivInfusionLocation === 'MEDICATION') {
        for (const locationMedId of check.ivInfusionLocationMedications || []) {
          const dosageForm = caseDosageFormsByMedicationId[locationMedId];
          if (!dosageForm) {
            errors.push(caseLogicMsg('Location medication not enabled', node.id, { errorId: 'locationMed', checkKey }));
          }
        }
      }

      // LOCATION - FLUID
      if (CHECKS_WITH_LOCATION.includes(check.type) && check.ivInfusionLocation === 'FLUID') {
        for (const locationFluidKey of check.ivInfusionLocationFluids || []) {
          const fluid = caseFluidsByFluidKey[locationFluidKey];
          if (!fluid) {
            errors.push(caseLogicMsg('Location fluid not enabled', node.id, { errorId: 'locationFluid', checkKey }));
          }
        }
      }

      // DRIPSET
      if (CHECKS_WITH_DRIPSET.includes(check.type) && check.method === 'DROP_SET') {
        const availableDripSets = get(caseConf, 'iv.dropSet.dripSet') || [];
        for (const dripSetKey of check.dripSets || []) {
          if (!availableDripSets.includes(dripSetKey)) {
            errors.push(caseLogicMsg('Drip set not enabled', node.id, { errorId: 'dripSet', checkKey }));
          }
        }
      }
    });
  });

  return {
    errors,
    warnings,
  };
}

export default validateInfusionNodes;
